/**
 * Editing one screen: what it is called, how big it is, and which way it is
 * turned on the wall.
 *
 * The update is a patch of what actually changed. That matters most for the
 * physical size, which the fleet row does not carry: the form reads it from the
 * screen record, and if that read fails it leaves the field alone rather than
 * sending a blank over a measurement somebody took with a tape.
 *
 * Rotation is editable forever, not only at claim time, because nobody knows
 * which way a TV was hung until they walk in and look at it. The change reaches
 * the device within one heartbeat and needs no re-pairing.
 *
 * Opening hours are the store's, but whether *this* screen keeps them is the
 * screen's own setting, because one shop can hold both a window display meant to
 * run all night and a menu board meant to go dark at closing.
 */

import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { screenUpdateSchema } from '@pbtv/shared';
import type { RotationDegrees } from '@pbtv/shared';
import { errorMessage } from '../../lib/api.js';
import { buildScreenPatch, getScreen, screenQueryKey } from '../../lib/screens.js';
import type { ScreenPatch } from '../../lib/screens.js';
import { rotationLabel } from '../preview/geometry.js';

const ROTATIONS: readonly RotationDegrees[] = [0, 90, 180, 270];

/**
 * `.segmented__item` carries the whole look of a segmented control, and
 * `.is-selected` is the only thing that marks which option is live — there is no
 * `[aria-pressed]` styling to fall back on. Omitting it renders two identical
 * default buttons, so a screen reader is told which mode is on and a person
 * looking at it is not.
 */
function segmentedClass(selected: boolean): string {
  return selected ? 'segmented__item is-selected' : 'segmented__item';
}

export interface ScreenEditFormProps {
  screenId: string;
  storeName: string;
  initialName: string;
  initialRotation: RotationDegrees;
  /** Whether this screen currently raises its own player. */
  initialAutoStart: boolean;
  /** Whether this screen currently ignores its store's opening hours. */
  initialAlwaysOn: boolean;
  busy: boolean;
  error: unknown;
  onSubmit: (patch: ScreenPatch) => void;
  onCancel: () => void;
}

export function ScreenEditForm({
  screenId,
  storeName,
  initialName,
  initialRotation,
  initialAutoStart,
  initialAlwaysOn,
  busy,
  error,
  onSubmit,
  onCancel,
}: ScreenEditFormProps): ReactElement {
  const detail = useQuery({
    queryKey: screenQueryKey(screenId),
    queryFn: ({ signal }) => getScreen(screenId, signal),
    retry: 0,
    staleTime: 0,
  });

  const [name, setName] = useState(initialName);
  const [rotation, setRotation] = useState<RotationDegrees>(initialRotation);
  const [autoStart, setAutoStart] = useState(initialAutoStart);
  const [alwaysOn, setAlwaysOn] = useState(initialAlwaysOn);
  const [size, setSize] = useState('');
  const [sizeTouched, setSizeTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  /** The stored size as a string, or null while it is unknown to this form. */
  const baselineSize = useMemo(() => {
    const record = detail.data;
    if (record === undefined) return null;
    const value = record.sizeInches;
    return value === null || value === undefined ? '' : String(value);
  }, [detail.data]);

  useEffect(() => {
    if (sizeTouched || baselineSize === null) return;
    setSize(baselineSize);
  }, [baselineSize, sizeTouched]);

  const patch = buildScreenPatch(
    { name, sizeInches: size, rotationDegrees: rotation, autoStart, alwaysOn },
    {
      name: initialName,
      sizeInches: baselineSize,
      rotationDegrees: initialRotation,
      autoStart: initialAutoStart,
      alwaysOn: initialAlwaysOn,
    },
  );
  const hasChanges = Object.keys(patch).length > 0;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!hasChanges) return;

    const parsed = screenUpdateSchema.safeParse(patch);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Check the values above.');
      return;
    }

    setValidationError(null);
    onSubmit(parsed.data);
  };

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <p className="form__context">
        {storeName} · <span className="mono">{screenId}</span>
      </p>

      <div className="form__grid">
        <label className="field">
          <span className="field__label">Screen name</span>
          <input
            className="input"
            type="text"
            required
            autoFocus
            maxLength={120}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <span className="field__hint">
            What staff call this TV — “Counter left”, “Window”. It shows on the idle card.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Physical size (inches)</span>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={1}
            max={200}
            step={0.5}
            placeholder={detail.isPending ? 'Reading…' : 'Not recorded'}
            disabled={detail.isPending}
            value={size}
            onChange={(event) => {
              setSizeTouched(true);
              setSize(event.currentTarget.value);
            }}
          />
          <span className="field__hint">
            {detail.isError
              ? 'The recorded size could not be read. Leave this blank to keep whatever is stored.'
              : 'The screen’s diagonal. Used for planning, not for playback.'}
          </span>
        </label>

        <label className="field field--span">
          <span className="field__label">Rotation</span>
          <select
            className="input"
            value={rotation}
            onChange={(event) =>
              setRotation(Number(event.currentTarget.value) as RotationDegrees)
            }
          >
            {ROTATIONS.map((value) => (
              <option key={value} value={value}>
                {value}° — {rotationLabel(value)}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Fire TV always sends a landscape picture down the cable, so the app turns its own
            canvas instead. The change reaches the TV within one heartbeat, with no re-pairing.
          </span>
        </label>

        <div className="field field--span">
          <span className="field__label">Auto-start</span>
          <div className="segmented" role="group" aria-label="Auto-start">
            <button
              type="button"
              className={segmentedClass(autoStart)}
              aria-pressed={autoStart}
              onClick={() => setAutoStart(true)}
            >
              On
            </button>
            <button
              type="button"
              className={segmentedClass(!autoStart)}
              aria-pressed={!autoStart}
              onClick={() => setAutoStart(false)}
            >
              Off
            </button>
          </div>
          <span className="field__hint">
            On means the stick puts the menu board up by itself — after a power cut, after an
            update, and if somebody presses HOME on the remote. Turning it on reaches the TV
            within a minute. Turning it off takes effect at the next start; the screen keeps
            playing until then.
          </span>
        </div>

        <div className="field field--span">
          <span className="field__label">Opening hours</span>
          <div className="segmented" role="group" aria-label="Opening hours">
            <button
              type="button"
              className={segmentedClass(!alwaysOn)}
              aria-pressed={!alwaysOn}
              onClick={() => setAlwaysOn(false)}
            >
              Follow {storeName}
            </button>
            <button
              type="button"
              className={segmentedClass(alwaysOn)}
              aria-pressed={alwaysOn}
              onClick={() => setAlwaysOn(true)}
            >
              Run 24 hours
            </button>
          </div>
          <span className="field__hint">
            A window screen facing the street usually wants 24 hours; a menu board above the
            counter usually wants to go dark when the shop does. Outside its hours a screen
            shows black but stays fully online — it keeps checking in, keeps downloading, and
            can still be told what to play. If the store has no opening hours set, both
            options run around the clock.
          </span>
        </div>
      </div>

      {!initialAlwaysOn && alwaysOn ? (
        <p className="alert alert--info" role="status">
          This screen will stop going dark at closing time, and it will be judged against the
          clock rather than against {storeName}&rsquo;s hours: if it goes quiet at 3am it
          shows here as a fault straight away, instead of being taken for closing time. It
          shows here — nothing is emailed out yet.
        </p>
      ) : null}

      {initialAlwaysOn && !alwaysOn ? (
        <p className="alert alert--warning" role="status">
          This screen will go back to following {storeName}&rsquo;s opening hours, so it will
          be black outside them and its overnight silence will read as closing time rather
          than as a fault. If the store has no hours set, both settings run around the clock
          and this changes only how the screen is watched.
        </p>
      ) : null}

      {initialAutoStart && !autoStart ? (
        <p className="alert alert--warning" role="status">
          This TV will stay dark after its next power cut, and pressing HOME on the remote will
          leave it on the Fire TV home screen. The stick keeps checking in either way, so
          turning auto-start back on from here is a click rather than a drive.
        </p>
      ) : null}

      {validationError !== null ? (
        <p className="alert alert--danger" role="alert">
          {validationError}
        </p>
      ) : null}

      {error !== null && error !== undefined ? (
        <p className="alert alert--danger" role="alert">
          {errorMessage(error)}
        </p>
      ) : null}

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button--primary" disabled={busy || !hasChanges}>
          {busy ? 'Saving…' : hasChanges ? 'Save changes' : 'No changes yet'}
        </button>
      </div>
    </form>
  );
}
